import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { referenceDataAPI, tagsAPI } from "../services/api";
import ConfirmationModal from "../Modal/ConfirmationModal";
import { useAuth } from "../context/AuthContext";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: min(720px, calc(100vw - 32px));
  height: 680px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  flex: 1;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  right: 0;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const ContentSection = styled.div`
  padding: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagContainer = styled.div`
  position: relative;
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
  overflow: visible;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagSuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 70px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  margin-top: 4px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 9999;
`;

const TagSuggestionItem = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f3f4f6;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const AddTagButton = styled.button`
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00b5b8;
  color: #00b5b8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00b5b8;
  cursor: pointer;
  color: #00b5b8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const GenderSection = styled.div`
  margin-bottom: 1rem;
`;

const GenderAgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
`;

const GenderColumn = styled.div`
  flex: 1;
`;

const AgeColumn = styled.div`
  flex: 1;
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RadioInput = styled.input`
  width: 0.9rem;
  height: 0.9rem;
  accent-color: #1d87f0;

  &:checked {
    background-color: #1d87f0;
    border-color: #1d87f0;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
`;

const AgeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
`;

const AgeInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AgeInput = styled.input`
  width: 70px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  text-align: center;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #1d87f0;
    box-shadow: 0 0 0 3px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const AgeSeparator = styled.span`
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const MultiSelectWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  height: fit-content;

  &:hover {
    background-color: #0891b2;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }
`;

const ParameterOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const CategoryHeader = styled.div`
  padding: 0.35rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #00b5b8;
  background-color: #f0fafa;
  border-top: 1px solid #e5e7eb;
  pointer-events: none;
  user-select: none;

  &:first-child {
    border-top: none;
  }
`;

const Checkbox = styled.input`
  width: 0.8rem;
  height: 0.8rem;
  accent-color: #00b5b8;
`;

const TableContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-top: 0.5rem;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background-color: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;

  &:nth-child(1) {
    width: 24%;
  } /* พารามิเตอร์ */
  &:nth-child(2) {
    width: 32%;
  } /* ค่าปกติ - ใหญ่กว่าเล็กน้อยเพราะมี 3 คอลัมน์ย่อย */
  &:nth-child(3) {
    width: 44%;
  } /* ค่าที่กำหนด */
`;

const TableCell = styled.td`
  padding: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
  vertical-align: top;
  text-align: center;

  /* ลบ padding สำหรับ cell ที่มีตารางย่อย */
  &.no-padding {
    padding: 0;
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
`;

const TableDualRangeContainer = styled.div`
  flex: 1;
  position: relative;
  height: 3px;
  min-width: 150px;
`;

const TableRangeTrack = styled.div`
  width: 100%;
  height: 3px;
  background: #e5e7eb;
  border-radius: 1.5px;
  position: relative;
  overflow: hidden;
`;

const TableRangeProgress = styled.div`
  height: 100%;
  background: #1d87f0;
  border-radius: 1.5px;
  position: absolute;
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  z-index: 1;
  pointer-events: none;
`;

const TableRangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 3px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;
  z-index: 3;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1d87f0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    margin-top: -3.5px;
    transform: translateY(-0.5px);
  }

  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1d87f0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -3.5px;
  }
`;

const TableRangeDisplay = styled.div`
  font-size: 0.75rem;
  color: #374151;
  min-width: 70px;
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
`;

const NormalRangeBadge = styled.span`
  background: transparent;
  color: #374151;
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: normal;
`;

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  table-layout: auto;
  margin: 0;
`;

const SubTableHeader = styled.thead`
  background-color: transparent;
`;

const SubTableRow = styled.tr`
  border-bottom: none;
`;

const SubTableHeaderCell = styled.th`
  padding: 0.4rem 0.3rem;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  border-right: none;
  white-space: nowrap;

  &:nth-child(1) {
    width: 20%;
  } /* เพศ */
  &:nth-child(2) {
    width: 40%;
  } /* ช่วงอายุ */
  &:nth-child(3) {
    width: 40%;
  } /* ค่าปกติ */
`;

const SubTableCell = styled.td`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  color: #374151;
  border-right: none;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: center;
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ParameterInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  justify-content: center;
  width: 100%;
`;

const ParameterInput = styled.input`
  width: 55px;
  padding: 0.25rem 0.3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.7rem;
  text-align: center;

  &::placeholder {
    color: #9ca3af;
    font-size: 0.65rem;
  }

  &:focus {
    outline: none;
    border-color: #1d87f0;
    box-shadow: 0 0 0 2px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const ParameterSeparator = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

const SaveButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #0891b2;
  }
`;

const CreateTemplateModal = ({ isOpen, onClose, onSave }) => {
  const { getUserFullName } = useAuth();
  const [templateName, setTemplateName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]); // tags ที่ผู้ใช้เลือกแล้ว
  const [availableTags, setAvailableTags] = useState([]); // tags ทั้งหมดที่มีในระบบ
  const [showTagSuggestions, setShowTagSuggestions] = useState(false); // แสดง/ซ่อน autocomplete
  const [selectedGender, setSelectedGender] = useState("male");
  const [ageRange, setAgeRange] = useState(["", ""]); // เริ่มต้นเป็นค่าว่าง ให้ user กรอกเอง
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [tempSelectedParameters, setTempSelectedParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [parameterRanges, setParameterRanges] = useState({});
  const [parameterRadioValues, setParameterRadioValues] = useState({}); // สำหรับเก็บค่า radio (ไม่มี/มี)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allParameters, setAllParameters] = useState([]);
  const [loading, setLoading] = useState(false);

  // Reset state เมื่อเปิด modal และตั้งค่าชื่อผู้สร้างอัตโนมัติ
  useEffect(() => {
    if (isOpen) {
      setTags([]); // clear tags ที่เลือก
      setTagInput(""); // clear input
      setShowTagSuggestions(false); // ซ่อน suggestions
      setCreatedBy(getUserFullName()); // ดึงชื่อผู้ใช้งานที่ login เข้ามาแสดงอัตโนมัติ
    }
  }, [isOpen, getUserFullName]);

  // ดึงข้อมูล tags ทั้งหมดจากฐานข้อมูล (template_tags) เก็บไว้ใน availableTags
  useEffect(() => {
    if (!isOpen) return;

    const loadTags = async () => {
      try {
        const response = await tagsAPI.getUsedTags();

        if (
          response.status === "success" &&
          response.data &&
          Array.isArray(response.data)
        ) {
          // แปลงเป็น array ของ tag names
          const tagNames = response.data.map((tag) => tag.tag_name);
          setAvailableTags(tagNames); // เก็บไว้ใน availableTags แทน tags
          console.log("✅ โหลด tags สำเร็จ:", tagNames);
        }
      } catch (error) {
        console.error("✗ Error loading tags:", error);
        // ถ้า error ใช้ mock data
        setAvailableTags([
          "ผู้สูงอายุ",
          "พันธุกรรม",
          "เบาหวาน",
          "ความดันโลหิตสูง",
        ]);
      }
    };

    loadTags();
  }, [isOpen]);

  // ดึงข้อมูลพารามิเตอร์จาก API เมื่อเพศหรืออายุเปลี่ยน
  useEffect(() => {
    if (!isOpen) return;

    // ป้องกัน race condition: ถ้ามี request ใหม่มา ให้ยกเลิก request เก่า
    let cancelled = false;

    const loadParameters = async () => {
      setLoading(true);
      try {
        // แปลงเพศจาก 'male'/'female'/'both' เป็น 'M'/'F'/null
        let gender = null;
        if (selectedGender === "male") gender = "M";
        else if (selectedGender === "female") gender = "F";
        // both = null (ดึงทั้งหมด)

        // ใช้อายุกลางของ range
        const minAge = parseFloat(ageRange[0]) || 0;
        const maxAge = parseFloat(ageRange[1]) || 999;
        const avgAge = (minAge + maxAge) / 2;

        // ส่งทั้ง age (สำหรับหาค่าที่เหมาะสมที่สุด) และ minAge, maxAge (สำหรับหาค่าทั้งหมดที่อยู่ในช่วง)
        // แปลง "both" เป็น null เพื่อให้ backend กรองทุกเพศ
        const response = await referenceDataAPI.getParametersList({
          gender: gender === "both" ? null : gender,
          age: avgAge,
          minAge: minAge,
          maxAge: maxAge,
        });

        // ถ้า request นี้ถูกยกเลิกแล้ว (มี request ใหม่มาแทน) ไม่ต้อง update state
        if (cancelled) return;

        // API Backend ส่งกลับมาในรูปแบบ { status: "success", message: "...", data: [...] }
        if (
          response.status === "success" &&
          response.data &&
          Array.isArray(response.data)
        ) {
          // API ส่งกลับมาเป็น array ของ objects โดยตรง
          const parameters = response.data.map((paramData) => {
            // normalRange มาจาก API แล้ว (เป็น array [min, max])
            const normalRange = paramData.normalRange || [0, 100];

            return {
              name: paramData.name,
              unit: paramData.unit || "",
              normalRange: normalRange,
              normalValue: paramData.normalValue,
              category: paramData.category,
              allNormalRanges: paramData.allNormalRanges || [], // เพิ่ม field ใหม่
            };
          });

          console.log("✅ Total parameters loaded:", parameters.length);
          setAllParameters(parameters);
        } else {
          setAllParameters([]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("✗ Error loading parameters:", error);

        // ใช้ข้อมูล mock data เพื่อทดสอบ UI
        const mockParameters = [
          {
            name: "Albumin",
            unit: "g/dL",
            normalRange: [3.8, 5.4],
            normalValue: "3.8-5.4",
            category: "การทำงานของตับ",
          },
          {
            name: "WBC",
            unit: "x10³/µL",
            normalRange: [4.5, 13.5],
            normalValue: "4.5-13.5",
            category: "เม็ดเลือดขาว",
          },
          {
            name: "Creatinine",
            unit: "mg/dL",
            normalRange: [0.55, 1.02],
            normalValue: "0.55-1.02",
            category: "การทำงานของไต",
          },
          {
            name: "HbA1C",
            unit: "%",
            normalRange: [0, 5.7],
            normalValue: "<5.7",
            category: "น้ำตาลในเลือด",
          },
        ];

        setAllParameters(mockParameters);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadParameters();

    // cleanup: ยกเลิก request เก่าเมื่อ dependency เปลี่ยน
    return () => {
      cancelled = true;
    };
  }, [selectedGender, ageRange, isOpen]);

  // 🔥 อัปเดตค่าปกติของพารามิเตอร์ที่เลือกไว้แล้ว เมื่อ allParameters เปลี่ยน (เพศ/อายุเปลี่ยน)
  useEffect(() => {
    if (selectedParameters.length === 0 || allParameters.length === 0) return;

    const updatedSelectedParams = selectedParameters.map((selectedParam) => {
      // หาพารามิเตอร์ที่ตรงกันใน allParameters ใหม่
      const updatedParam = allParameters.find(
        (p) => p.name === selectedParam.name,
      );
      if (updatedParam) {
        // อัปเดตค่าปกติและหน่วย แต่เก็บ category เดิมไว้
        return {
          ...selectedParam,
          normalRange: updatedParam.normalRange,
          normalValue: updatedParam.normalValue,
          unit: updatedParam.unit,
          allNormalRanges: updatedParam.allNormalRanges || [], // อัปเดต allNormalRanges ด้วย
        };
      }
      return selectedParam;
    });

    // เช็คว่ามีการเปลี่ยนแปลงจริงๆ ก่อน setState เพื่อหลีกเลี่ยง re-render ที่ไม่จำเป็น
    const hasChanges = updatedSelectedParams.some((param, index) => {
      const oldParam = selectedParameters[index];

      return (
        param.normalValue !== oldParam.normalValue ||
        param.normalRange[0] !== oldParam.normalRange[0] ||
        param.normalRange[1] !== oldParam.normalRange[1] ||
        JSON.stringify(param.allNormalRanges) !==
          JSON.stringify(oldParam.allNormalRanges)
      );
    });

    if (hasChanges) {
      console.log("🔄 Updating selectedParameters with allNormalRanges");
      setSelectedParameters(updatedSelectedParams);
    }
  }, [allParameters]);

  const addTag = (tagName = null) => {
    const tagToAdd = tagName || tagInput.trim();
    if (tagToAdd && !tags.includes(tagToAdd)) {
      setTags([...tags, tagToAdd]);
      setTagInput("");
      setShowTagSuggestions(false);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  // ฟังก์ชันทำความสะอาดชื่อพารามิเตอร์ (ตัด _hide_ prefix ออก)
  const cleanParameterName = (name) => {
    if (name && name.startsWith("_hide_")) {
      return name.substring(6); // ตัด "_hide_" (6 ตัวอักษร) ออก
    }
    return name;
  };

  // กรอง tags ที่ตรงกับคำค้นหา และยังไม่ได้เลือก
  const filteredTags = availableTags.filter(
    (tag) =>
      tag.toLowerCase().includes(tagInput.toLowerCase()) && !tags.includes(tag),
  );

  // แสดง autocomplete เมื่อมีการพิมพ์
  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);
    setShowTagSuggestions(value.trim().length > 0);
  };

  const handleMinAgeChange = (e) => {
    let value = e.target.value;
    // อนุญาตให้พิมพ์ค่าว่าง หรือทศนิยม
    if (value === "" || value === ".") {
      setAgeRange([value, ageRange[1]]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // จำกัดทศนิยมไม่เกิน 3 ตำแหน่ง
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([roundedValue, ageRange[1]]); // ไม่บังคับให้ค่าต้องเรียงกัน
    }
  };

  const handleMaxAgeChange = (e) => {
    let value = e.target.value;
    // อนุญาตให้พิมพ์ค่าว่าง หรือทศนิยม
    if (value === "" || value === ".") {
      setAgeRange([ageRange[0], value]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // จำกัดทศนิยมไม่เกิน 3 ตำแหน่ง
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([ageRange[0], roundedValue]); // ไม่บังคับให้ค่าต้องเรียงกัน
    }
  };

  const filteredParameters = allParameters.filter((param) =>
    param.normalValue !== null && param.normalValue !== '' &&
    !["Urine Glucose", "น้ำตาล (Sugar)"].includes(param.name) &&
    param.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleParameterToggle = (parameter) => {
    const isSelected = tempSelectedParameters.some(
      (p) => p.name === parameter.name,
    );
    if (isSelected) {
      setTempSelectedParameters(
        tempSelectedParameters.filter((p) => p.name !== parameter.name),
      );
    } else {
      setTempSelectedParameters([...tempSelectedParameters, parameter]);
    }
  };

  // ฟังก์ชันตรวจสอบว่าพารามิเตอร์เป็นชนิด radio (ไม่มี/มี) หรือไม่
  const isRadioParameter = (parameter) => {
    const radioParams = [
      "Urine Appearance",
      "Color",
      "Clarity",
      "Color (Stool)",
      "Appearance (Stool)",
      "Parasite",
      "RBC Index",
    ];
    return radioParams.includes(parameter.name);
  };

  const shouldHideValueInput = (parameter) => {
    return parameter.name === "Color (Stool)";
  };

  const isDropdownParameter = (parameter) => {
    const dropdownParams = [
      "Protein", "Sugar", "Glucose", "Ketones", "Urine Ketone", "Blood",
      "Leukocytes", "Nitrite", "Bilirubin", "Urobilinogen", "Bacteria",
    ];
    return dropdownParams.includes(parameter.name);
  };

  const isNegPosParameter = (parameter) => {
    const negPosParams = [
      "HBsAb", "HBsAg", "HCV Ab", "Hemoglobin detection stool",
    ];
    return negPosParams.includes(parameter.name);
  };

  const isCountDropdownParameter = (parameter) => {
    const countParams = [
      "RBC (Urine)", "Red Blood Cells",
      "WBC (Urine)", "White Blood Cells",
      "WBC (Stool)", "RBC (Stool)",
      "Epithelial cell", "เซลล์เยื่อบุผิว",
      "_hide_Non-squamous epithelial cell",
      "_hide_Renal tubular epithelial cell",
      "_hide_Calcium oxalate crystal",
      "_hide_Uric acid crystal",
      "_hide_Triple phosphate crystal",
      "_hide_Calcium Carbonate crystal",
      "_hide_Calcium phosphate crystal",
      "_hide_Cystine crystal",
    ];
    return countParams.includes(parameter.name);
  };

  const DROPDOWN_OPTIONS = ["Negative", "Trace", "1+", "2+", "3+", "4+"];
  const NEG_POS_OPTIONS = ["Negative", "Positive"];
  const COUNT_DROPDOWN_OPTIONS = ["0-1", "1-2", "2-3", "3-5", "5-10", "10-20", "20-30", "30-50", "50-100", "100-200", ">200"];

  // ฟังก์ชันตรวจสอบว่าพารามิเตอร์ควรซ่อนข้อมูลเพศ/อายุ (ไม่แสดง sub-table)
  const shouldHideGenderAgeTable = (parameter) => {
    const hideTableParams = [
      // ไม่มีพารามิเตอร์ที่ต้องซ่อน - แสดง sub-table ทั้งหมด
    ];
    return hideTableParams.includes(parameter.name);
  };

  // ฟังก์ชันเพื่อดึงตัวเลือกสำหรับ radio button แต่ละพารามิเตอร์
  const getRadioOptions = (parameterName) => {
    const optionsMap = {
      "Urine Appearance": ["Clear", "ผิดปกติ"],
      Color: ["เหลืองอ่อน", "ผิดปกติ"],
      Clarity: ["Clear", "ผิดปกติ"],
      "Color (Stool)": ["xxx"],
      "Appearance (Stool)": ["xxx"],
      "WBC (Stool)": ["Not found", "Found"],
      "RBC (Stool)": ["Not found", "Found"],
      Parasite: ["Not found", "Found"],
      "RBC Index": ["xxx"],
      eGFR: ["xxx"],
    };
    return optionsMap[parameterName] || ["ไม่มี", "มี"];
  };

  const handleSelectParameters = () => {
    setSelectedParameters([...tempSelectedParameters]);

    const newRanges = { ...parameterRanges };
    const newRadioValues = { ...parameterRadioValues };

    Object.keys(newRanges).forEach((paramName) => {
      if (!tempSelectedParameters.some((p) => p.name === paramName)) {
        delete newRanges[paramName];
      }
    });

    Object.keys(newRadioValues).forEach((paramName) => {
      if (!tempSelectedParameters.some((p) => p.name === paramName)) {
        delete newRadioValues[paramName];
      }
    });

    tempSelectedParameters.forEach((parameter) => {
      if (isRadioParameter(parameter)) {
        // ถ้าเป็น radio parameter ให้ตั้งค่าเริ่มต้นเป็นตัวเลือกแรก
        if (!newRadioValues[parameter.name]) {
          const options = getRadioOptions(parameter.name);
          newRadioValues[parameter.name] = options[0];
        }
      } else if (isNegPosParameter(parameter)) {
        // NegPos parameter เก็บเป็น single value ใน radioValues
        if (!newRadioValues[parameter.name]) {
          newRadioValues[parameter.name] = "Negative";
        }
      } else if (isCountDropdownParameter(parameter)) {
        // Count dropdown parameter เก็บเป็น range ใน parameterRanges (2 dropdowns)
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["0-1", "0-1"];
        }
      } else {
        // ถ้าเป็น range parameter ให้เริ่มต้นเป็นค่าว่าง
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = isDropdownParameter(parameter) ? ["Negative", "Negative"] : ["", ""];
        }
      }
    });

    setParameterRanges(newRanges);
    setParameterRadioValues(newRadioValues);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const handleDropdownOpen = () => {
    setTempSelectedParameters([...selectedParameters]);
    setIsDropdownOpen(true);
  };

  // ฟังก์ชันสร้าง placeholder จาก normalValue
  const getPlaceholderFromNormalValue = (normalValue) => {
    if (!normalValue) return ["", ""];

    const trimmed = normalValue.trim();

    // กรณี >50 → placeholder: "0-50"
    if (trimmed.startsWith(">")) {
      const val = trimmed.substring(1);
      return ["0", val];
    }
    // กรณี <130 → placeholder: "0-130"
    else if (trimmed.startsWith("<")) {
      const val = trimmed.substring(1);
      return ["0", val];
    }
    // กรณี 3.5-7.2 → placeholder: "3.5" และ "7.2"
    else if (trimmed.includes("-")) {
      const parts = trimmed.split("-");
      return parts.length === 2 ? [parts[0], parts[1]] : ["", ""];
    }
    // กรณีเป็นตัวเลขเดียว
    else {
      return [trimmed, trimmed];
    }
  };

  // ฟังก์ชันกำหนด placeholder เฉพาะสำหรับพารามิเตอร์บางตัว
  const getCustomPlaceholder = (paramName) => {
    // กลุ่มที่ 1: placeholder ช่องแรก "0", ช่องที่สอง "+4"
    const group1Params = [
      "Protein",
      "Sugar",
      "Glucose",
      "Ketones",
      "Blood",
      "Leukocytes",
      "Nitrite",
      "Bilirubin",
      "Urobilinogen",
      "Bacteria",
    ];

    // กลุ่มที่ 2: placeholder ช่องแรก "0", ช่องที่สอง ">200"
    const group2Params = [
      "Red Blood Cells",
      "White Blood Cells",
      "RBC (Urine)",
      "WBC (Urine)",
      "เซลล์เยื่อบุผิว",
      "Epithelial cell",
    ];

    if (group1Params.includes(paramName)) {
      return ["0", "4+"];
    }

    if (group2Params.includes(paramName)) {
      return ["0", ">200"];
    }

    return null; // ไม่ใช่พารามิเตอร์พิเศษ ให้ใช้ค่า default
  };

  const handleParameterRangeChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || [0, 100];

    // อนุญาตให้พิมพ์ค่าว่าง
    if (value === "" || value === ".") {
      const newRange = isMin
        ? [value, currentRange[1]]
        : [currentRange[0], value];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange,
      });
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const newRange = isMin
        ? [numValue, currentRange[1]]
        : [currentRange[0], numValue];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange,
      });
    }
  };

  const handleDropdownChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || ["Negative", "Negative"];
    const newRange = isMin ? [value, currentRange[1]] : [currentRange[0], value];
    setParameterRanges({ ...parameterRanges, [paramName]: newRange });
  };

  const handleRadioChange = (paramName, value) => {
    setParameterRadioValues({
      ...parameterRadioValues,
      [paramName]: value,
    });
  };

  const handleSave = () => {
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    const newTemplate = {
      templateName,
      createdBy,
      tags,
      gender: selectedGender,
      ageRange,
      selectedParameters,
      parameterRanges,
      parameterRadioValues,
    };
    onSave(newTemplate);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>สร้าง Template</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentSection>
            <FormGroup>
              <Label>ชื่อ Template</Label>
              <Input
                type="text"
                placeholder="ชื่อ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ชื่อผู้ตรวจ</Label>
              <Input
                type="text"
                placeholder="ชื่อผู้ตรวจ"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                disabled
                style={{ backgroundColor: "#F9FAFB", cursor: "not-allowed" }}
              />
            </FormGroup>

            <FormGroup>
              <Label>ป้ายกำกับ (Tag)</Label>
              <TagContainer>
                <TagInputContainer>
                  <TagInput
                    type="text"
                    placeholder="พิมพ์เพื่อค้นหา tag..."
                    value={tagInput}
                    onChange={handleTagInputChange}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    onFocus={() =>
                      setShowTagSuggestions(tagInput.trim().length > 0)
                    }
                    onBlur={() =>
                      setTimeout(() => setShowTagSuggestions(false), 200)
                    }
                  />
                  <AddTagButton onClick={() => addTag()}>เพิ่ม</AddTagButton>

                  {showTagSuggestions && filteredTags.length > 0 && (
                    <TagSuggestionsDropdown>
                      {filteredTags.map((tag, index) => (
                        <TagSuggestionItem
                          key={index}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addTag(tag);
                          }}
                        >
                          {tag}
                        </TagSuggestionItem>
                      ))}
                    </TagSuggestionsDropdown>
                  )}
                </TagInputContainer>

                {tags.length > 0 && (
                  <TagList>
                    {tags.map((tag, index) => (
                      <Tag key={index}>
                        <span>{tag}</span>
                        <TagRemoveButton onClick={() => removeTag(tag)}>
                          <X size={8} />
                        </TagRemoveButton>
                      </Tag>
                    ))}
                  </TagList>
                )}
              </TagContainer>
            </FormGroup>

            <GenderSection>
              <GenderAgeRow>
                <GenderColumn>
                  <Label>สำหรับเพศ</Label>
                  <GenderOptions>
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={selectedGender === "male"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="male">ชาย</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={selectedGender === "female"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="female">หญิง</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="both"
                        name="gender"
                        value="both"
                        checked={selectedGender === "both"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="both">ทุกเพศ</RadioLabel>
                    </RadioContainer>
                  </GenderOptions>
                </GenderColumn>

                <AgeColumn>
                  <Label>ช่วงอายุ (ปี)</Label>
                  <AgeRangeContainer>
                    <AgeInputWrapper>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="0.000"
                        value={ageRange[0]}
                        onChange={handleMinAgeChange}
                      />
                      <AgeSeparator>-</AgeSeparator>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="999"
                        value={ageRange[1]}
                        onChange={handleMaxAgeChange}
                      />
                    </AgeInputWrapper>
                  </AgeRangeContainer>
                </AgeColumn>
              </GenderAgeRow>
            </GenderSection>

            <FormGroup>
              <Label>เลือกพารามิเตอร์</Label>
              <MultiSelectContainer>
                <MultiSelectWrapper>
                  <MultiSelectButton type="button" onClick={handleDropdownOpen}>
                    <span>
                      {selectedParameters.length === 0
                        ? "เลือกพารามิเตอร์"
                        : `${selectedParameters.length} selected`}
                    </span>
                    <span>{isDropdownOpen ? "▲" : "▼"}</span>
                  </MultiSelectButton>

                  {isDropdownOpen && (
                    <DropdownContent>
                      <SearchInput
                        type="text"
                        placeholder="ค้นหาพารามิเตอร์..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {loading ? (
                        <ParameterOption
                          style={{ justifyContent: "center", color: "#6B7280" }}
                        >
                          กำลังโหลดพารามิเตอร์...
                        </ParameterOption>
                      ) : filteredParameters.length === 0 ? (
                        <ParameterOption
                          style={{ justifyContent: "center", color: "#6B7280" }}
                        >
                          {searchTerm
                            ? "ไม่พบพารามิเตอร์ที่ค้นหา"
                            : "ไม่มีพารามิเตอร์"}
                        </ParameterOption>
                      ) : (
                        (() => {
                          // จัดกลุ่มตาม category โดยรักษาลำดับ
                          const groups = [];
                          const seen = new Map();
                          filteredParameters.forEach((param) => {
                            const cat = param.category || 'อื่นๆ';
                            if (!seen.has(cat)) {
                              seen.set(cat, []);
                              groups.push({ category: cat, params: seen.get(cat) });
                            }
                            seen.get(cat).push(param);
                          });
                          return groups.map(({ category, params }) => (
                            <React.Fragment key={category}>
                              <CategoryHeader>{category}</CategoryHeader>
                              {params.map((parameter, index) => (
                                <ParameterOption
                                  key={index}
                                  onClick={() => handleParameterToggle(parameter)}
                                >
                                  <Checkbox
                                    type="checkbox"
                                    checked={tempSelectedParameters.some(
                                      (p) => p.name === parameter.name,
                                    )}
                                    onChange={() => {}}
                                  />
                                  <span>
                                    {cleanParameterName(parameter.name)} (
                                    {parameter.unit})
                                  </span>
                                </ParameterOption>
                              ))}
                            </React.Fragment>
                          ));
                        })()
                      )}
                    </DropdownContent>
                  )}
                </MultiSelectWrapper>

                <SelectButton onClick={handleSelectParameters}>
                  เลือก
                </SelectButton>
              </MultiSelectContainer>
            </FormGroup>

            <TableContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>พารามิเตอร์</TableHeaderCell>
                    <TableHeaderCell>ค่าปกติ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่กำหนด</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {selectedParameters.length > 0 ? (
                    selectedParameters.map((param, index) => {
                      const isRadio = isRadioParameter(param);
                      const isNegPos = isNegPosParameter(param);
                      const isCountDrop = isCountDropdownParameter(param);

                      if (isNegPos) {
                        // แสดง dropdown Negative/Positive สำหรับพารามิเตอร์ประเภทนี้
                        const negPosValue = parameterRadioValues[param.name] || "Negative";
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>{cleanParameterName(param.name)}</TableCell>
                            <TableCell className={allNormalRanges.length > 0 && !hideGenderAge ? "no-padding" : ""}>
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ช่วงอายุ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>{range.age_range}</SubTableCell>
                                        <SubTableCell>{range.normal_range}</SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>{param.normalValue ?? "null"}</NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              <select
                                value={negPosValue}
                                onChange={(e) =>
                                  handleRadioChange(param.name, e.target.value)
                                }
                                style={{ padding: "0.25rem 0.5rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.8rem", cursor: "pointer" }}
                              >
                                {NEG_POS_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (isCountDrop) {
                        // แสดง dropdown 2 ฝั่ง สำหรับช่วงนับจำนวน (0-1, 1-2, ...)
                        const countRange = parameterRanges[param.name] || ["0-1", "0-1"];
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>{cleanParameterName(param.name)}</TableCell>
                            <TableCell className={allNormalRanges.length > 0 && !hideGenderAge ? "no-padding" : ""}>
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ช่วงอายุ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>{range.age_range}</SubTableCell>
                                        <SubTableCell>{range.normal_range}</SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>{param.normalValue ?? "null"}</NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              <ParameterInputWrapper>
                                <select
                                  value={countRange[0] || "0-1"}
                                  onChange={(e) => handleDropdownChange(param.name, true, e.target.value)}
                                  style={{ width: "auto", minWidth: "96px", maxWidth: "100%", flex: "0 0 auto", boxSizing: "border-box", padding: "0.25rem 0.25rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                  >
                                  {COUNT_DROPDOWN_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ParameterSeparator>-</ParameterSeparator>
                                <select
                                  value={countRange[1] || "0-1"}
                                  onChange={(e) => handleDropdownChange(param.name, false, e.target.value)}
                                  style={{ width: "auto", minWidth: "96px", maxWidth: "100%", flex: "0 0 auto", boxSizing: "border-box", padding: "0.25rem 0.25rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                  >
                                  {COUNT_DROPDOWN_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </ParameterInputWrapper>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (isRadio) {
                        // แสดง radio button สำหรับพารามิเตอร์ที่เป็น text-based
                        const options = getRadioOptions(param.name);
                        const radioValue =
                          parameterRadioValues[param.name] || options[0];
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);
                        const hideValueInput = shouldHideValueInput(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              {cleanParameterName(param.name)}
                            </TableCell>
                            <TableCell
                              className={
                                allNormalRanges.length > 0 && !hideGenderAge
                                  ? "no-padding"
                                  : ""
                              }
                            >
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>
                                        เพศ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ช่วงอายุ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ค่าปกติ
                                      </SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>
                                          {range.age_range}
                                        </SubTableCell>
                                        <SubTableCell>
                                          {range.normal_range}
                                        </SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>
                                  {param.normalValue ?? "null"}
                                </NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              {hideValueInput ? (
                                "-"
                              ) : (
                                <RadioOptionsContainer>
                                  {options.map((option, optionIndex) => (
                                    <RadioContainer key={optionIndex}>
                                      <RadioInput
                                        type="radio"
                                        id={`${param.name}-${optionIndex}`}
                                        name={param.name}
                                        value={option}
                                        checked={radioValue === option}
                                        onChange={(e) =>
                                          handleRadioChange(
                                            param.name,
                                            e.target.value,
                                          )
                                        }
                                      />
                                      <RadioLabel
                                        htmlFor={`${param.name}-${optionIndex}`}
                                      >
                                        {option}
                                      </RadioLabel>
                                    </RadioContainer>
                                  ))}
                                </RadioOptionsContainer>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // แสดง input fields สำหรับพารามิเตอร์ปกติ
                        const range = parameterRanges[param.name] || ["", ""];
                        // ตรวจสอบ placeholder แบบกำหนดเองก่อน ถ้าไม่มีค่อยใช้ค่าจาก normalValue
                        const customPlaceholder = getCustomPlaceholder(
                          param.name,
                        );
                        const [minPlaceholder, maxPlaceholder] =
                          customPlaceholder ||
                          getPlaceholderFromNormalValue(param.normalValue);
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);

                        // ✅ พารามิเตอร์ที่ใช้ dropdown
                        const isDropdown = isDropdownParameter(param);

                        // 🔍 Debug: แสดงข้อมูล allNormalRanges ตอน render
                        if (
                          param.name === "Hemoglobin" ||
                          param.name === "Hematocrit"
                        ) {
                          console.log(
                            "🎨 Rendering",
                            param.name,
                            "- allNormalRanges.length:",
                            allNormalRanges.length,
                          );
                          console.log("   Data:", allNormalRanges);
                        }

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              {cleanParameterName(param.name)}
                            </TableCell>
                            <TableCell
                              className={
                                allNormalRanges.length > 0 && !hideGenderAge
                                  ? "no-padding"
                                  : ""
                              }
                            >
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>
                                        เพศ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ช่วงอายุ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ค่าปกติ
                                      </SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>
                                          {range.age_range}
                                        </SubTableCell>
                                        <SubTableCell>
                                          {range.normal_range}
                                        </SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>
                                  {param.normalValue ?? "null"}
                                </NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              {isDropdown ? (
                                <ParameterInputWrapper>
                                  <select
                                      value={range[0] || "Negative"}
                                      onChange={(e) => handleDropdownChange(param.name, true, e.target.value)}
                                      style={{ width: "auto", minWidth: "110px", maxWidth: "100%", flex: "0 0 auto", boxSizing: "border-box", padding: "0.25rem 0.25rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                    >
                                    {DROPDOWN_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <select
                                      value={range[1] || "Negative"}
                                      onChange={(e) => handleDropdownChange(param.name, false, e.target.value)}
                                      style={{ width: "auto", minWidth: "110px", maxWidth: "100%", flex: "0 0 auto", boxSizing: "border-box", padding: "0.25rem 0.25rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                    >
                                    {DROPDOWN_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                </ParameterInputWrapper>
                              ) : (
                                <ParameterInputWrapper>
                                  <ParameterInput
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={minPlaceholder}
                                    value={range[0]}
                                    onChange={(e) =>
                                      handleParameterRangeChange(
                                        param.name,
                                        true,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <ParameterInput
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={maxPlaceholder}
                                    value={range[1]}
                                    onChange={(e) =>
                                      handleParameterRangeChange(
                                        param.name,
                                        false,
                                        e.target.value,
                                      )
                                    }
                                  />
                                </ParameterInputWrapper>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    })
                  ) : (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </ContentSection>

          <ActionSection>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SaveButton onClick={handleSave}>สร้าง Template</SaveButton>
          </ActionSection>
        </ModalBody>
      </ModalContainer>

      <ConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
        message="ยืนยันการสร้าง Template"
      />
    </ModalOverlay>
  );
};

export default CreateTemplateModal;
